/**
 * Vue transversale des mouvements, par géozone (`GET /v1/mouvements`, `filter[zone]` obligatoire —
 * `05-api-backend.md`). Sans zone sélectionnée, rien n'est demandé au serveur : la route rejetterait
 * l'appel, et une erreur de validation au premier chargement de l'écran serait déroutante.
 */
import { useMemo, useState } from "react";

import { Chargement, Erreur, Localisation, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDate } from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { Mouvement } from "../../shared/api/types";
import { useZones } from "../zones/requetes";
import { useMouvementsDeZone } from "./requetes";

function zoneTexte(nom: string | null | undefined, agregee: boolean | null | undefined): string {
  if (!nom) return "";
  return agregee ? `${nom} (localisation agrégée)` : nom;
}

type Colonne = "numero_grv" | "zone_quittee_nom" | "sortie" | "zone_entree_nom" | "entree";

// Un mouvement ne porte qu'un seul horodatage côté API (`OngletMouvements.tsx`) : trier sur la
// sortie ou sur l'entrée revient donc au même, mais les deux colonnes restent triables séparément
// pour un comportement uniforme avec les autres tableaux de l'application.
function valeurDe(mouvement: Mouvement, colonne: Colonne): string | null | undefined {
  switch (colonne) {
    case "numero_grv":
      return mouvement.numero_grv;
    case "zone_quittee_nom":
      return mouvement.zone_quittee_nom;
    case "zone_entree_nom":
      return mouvement.zone_entree_nom;
    case "sortie":
    case "entree":
      return mouvement.timestamp;
  }
}

export function EcranMouvements() {
  const [zone, definirZone] = useState<string>("");
  const zones = useZones();
  const mouvements = useMouvementsDeZone(zone || undefined);
  const { tri, basculerTri } = useTriTableau<Colonne>({ colonne: "sortie", descendant: true });

  const donnees = useMemo(() => mouvements.data?.data ?? [], [mouvements.data]);
  const triees = useTrie(donnees, tri, valeurDe);

  return (
    <section>
      <h1>Mouvements</h1>

      <div className="barre-de-filtres">
        <label htmlFor="filtre-zone">Géozone</label>
        <select id="filtre-zone" value={zone} onChange={(e) => definirZone(e.target.value)}>
          <option value="">Choisir une géozone…</option>
          {zones.data?.data.map((z) => (
            <option key={z.zone_id} value={z.zone_id}>
              {z.nom}
            </option>
          ))}
        </select>

        {zone && (
          <BoutonExporterCsv
            nomFichier="mouvements"
            colonnes={[
              { libelle: "GRV", valeur: (m: Mouvement) => m.numero_grv },
              {
                libelle: "Zone quittée",
                valeur: (m: Mouvement) => zoneTexte(m.zone_quittee_nom, m.localisation_agregee),
              },
              {
                libelle: "Date/heure de sortie",
                valeur: (m: Mouvement) => (m.zone_quittee_nom ? formaterDate(m.timestamp) : ""),
              },
              {
                libelle: "Zone d'entrée",
                valeur: (m: Mouvement) => zoneTexte(m.zone_entree_nom, m.localisation_agregee),
              },
              { libelle: "Date/heure d'entrée", valeur: (m: Mouvement) => formaterDate(m.timestamp) },
            ]}
            lignes={triees}
          />
        )}
      </div>

      {!zone && <Vide libelle="Sélectionner une géozone pour voir ses mouvements." />}
      {zone && mouvements.isPending && <Chargement libelle="Chargement des mouvements…" />}
      {zone && mouvements.isError && (
        <Erreur erreur={mouvements.error} reessayer={() => void mouvements.refetch()} />
      )}
      {zone && mouvements.data && triees.length === 0 && (
        <Vide libelle="Aucun mouvement enregistré sur cette géozone." />
      )}
      {zone && mouvements.data && triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable colonne="numero_grv" libelle="GRV" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable
                colonne="zone_quittee_nom"
                libelle="Zone quittée"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="sortie"
                libelle="Date/heure de sortie"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="zone_entree_nom"
                libelle="Zone d'entrée"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="entree"
                libelle="Date/heure d'entrée"
                tri={tri}
                basculerTri={basculerTri}
              />
            </tr>
          </thead>
          <tbody>
            {triees.map((mouvement, index) => (
              <tr key={`${mouvement.numero_grv}-${mouvement.timestamp}-${index}`}>
                <td>{mouvement.numero_grv}</td>
                <td>
                  {mouvement.zone_quittee_nom ? (
                    <Localisation zone={mouvement.zone_quittee_nom} agregee={mouvement.localisation_agregee} />
                  ) : (
                    "—"
                  )}
                </td>
                <td>{mouvement.zone_quittee_nom ? formaterDate(mouvement.timestamp) : "—"}</td>
                <td>
                  <Localisation zone={mouvement.zone_entree_nom} agregee={mouvement.localisation_agregee} />
                </td>
                <td>{formaterDate(mouvement.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
